// SAD-857: the program glossary, read once per program. One GetProgramGlossary
// call serves every page of the program (the key is shared and outside the
// ['cm', programId] prefix, so contract-model writes do not refetch it); the
// glossary changes when a document is read, or when someone records a link.
import { ContractModelTypes as CM } from '@mono/proto-clients';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { contractModelClient } from '../../../lib/connect/clients';
import { buildEntries, buildMatcher } from './matcher';
export const GLOSSARY_KEY = 'program glossary';
const STALE = 30 * 60000;
export const glossaryKey = (programId)=>[
        GLOSSARY_KEY,
        programId
    ];
export function useProgramGlossary(programId) {
    return useQuery({
        queryKey: glossaryKey(programId ?? ''),
        enabled: !!programId,
        staleTime: STALE,
        gcTime: 2 * STALE,
        refetchOnWindowFocus: false,
        queryFn: async ()=>{
            const resp = await contractModelClient.getProgramGlossary({
                body: {
                    programId
                }
            });
            return resp.body ?? new CM.GetProgramGlossaryResponse();
        }
    });
}
/** The glossary's entries and its matcher, built once per fetched glossary. */ export function useGlossaryModel(programId) {
    const q = useProgramGlossary(programId);
    const data = q.data;
    const model = useMemo(()=>{
        const entries = data ? buildEntries(data.terms) : [];
        return {
            entries,
            matcher: entries.length ? buildMatcher(entries) : null,
            sectionsRead: data?.sections?.verified ?? 0
        };
    }, [
        data
    ]);
    return {
        ...model,
        isLoading: q.isLoading,
        error: q.error
    };
}
/** The one glossary-link write: the page's drawer and the chat card's Accept. */ export async function addGlossaryLink(programId, link, options) {
    const resp = await contractModelClient.addGlossaryRelation({
        body: {
            programId,
            ...link
        }
    }, options);
    return resp.body?.relation;
}
export function useAddGlossaryLink(programId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (link)=>addGlossaryLink(programId, link),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: glossaryKey(programId)
            })
    });
}
export function useRemoveGlossaryLink(programId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (relationId)=>contractModelClient.rejectGlossaryRelation({
                body: {
                    programId,
                    relationId
                }
            }),
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: glossaryKey(programId)
            })
    });
}
